/**
 * AdminCreditPurchases — /admin/credit-purchases
 *
 * Lists every credit-pack purchase transaction with a Refund button
 * that calls the existing `/api/admin/payments/{txn_id}/refund` endpoint.
 * The refund endpoint auto-reverses the granted credits (clamped to
 * what's still in the user's bonus pool) so the ledger stays honest.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import { RefreshCw, RotateCcw, CheckCircle2, AlertTriangle } from 'lucide-react';

const STATUS_TONE = {
  paid:                'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  refunded:            'bg-rose-500/15 text-rose-700 border-rose-500/30',
  partially_refunded:  'bg-amber-500/15 text-amber-700 border-amber-500/30',
  pending:             'bg-sky-500/15 text-sky-700 border-sky-500/30',
};

export default function AdminCreditPurchases() {
  const { API } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refunding, setRefunding] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/credit-purchases`);
      setRows(data.rows || []);
    } catch (_) {
      toast.error('Could not load credit purchases');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const refund = async (r) => {
    if (!window.confirm(`Refund ${r.email} for $${(r.amount_cents / 100).toFixed(2)}? This will also reverse the granted credits (or as many as are still available in their bonus pool).`)) return;
    try {
      setRefunding(r.id);
      const { data } = await axios.post(`${API}/admin/payments/${r.id}/refund`, {
        reason: 'Admin credit-pack refund',
      });
      const cr = data?.credit_reversal || {};
      if (cr.attempted && cr.ok) {
        const msg = cr.shortfall
          ? `Refunded · reversed ${cr.reversed} credits (${cr.shortfall} shortfall — already spent)`
          : `Refunded · reversed ${cr.reversed} credits`;
        toast.success(msg);
      } else {
        toast.success('Refunded');
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Refund failed');
    } finally { setRefunding(null); }
  };

  return (
    <div className="container mx-auto px-4 sm:px-6 py-8 max-w-5xl" data-testid="admin-credit-purchases-page">
      <div className="flex items-start gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
          <CheckCircle2 className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Billing</div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground">Credit purchases</h1>
          <p className="text-sm text-muted-foreground">
            Every credit-pack purchase. Refunding also reverses the granted credits so ledgers stay balanced.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="rounded-full" data-testid="admin-credit-purchases-refresh">
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {loading && <div className="text-center py-16 text-sm text-muted-foreground">Loading purchases…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-center py-16 border border-dashed border-border rounded-2xl text-sm text-muted-foreground" data-testid="admin-credit-purchases-empty">
          No credit-pack purchases yet.
        </div>
      )}

      <ul className="space-y-2">
        {rows.map((r) => {
          const item = (r.items && r.items[0]) || {};
          const refunded = r.payment_status === 'refunded' || r.payment_status === 'partially_refunded';
          const isPending = r.payment_status !== 'paid' && !refunded;
          return (
            <li key={r.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`admin-credit-purchase-${r.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest font-black rounded-full px-2 py-0.5 border ${STATUS_TONE[r.payment_status] || 'border-border'}`}>
                      {r.payment_status}
                    </span>
                    <span className="font-heading font-extrabold text-base">
                      {item.label || item.pack_id || 'Credit pack'}
                    </span>
                    <span className="text-xs text-muted-foreground">· {item.credits?.toLocaleString?.() || 0} credits</span>
                    {item.percent_off > 0 && (
                      <span className="text-[9px] uppercase tracking-widest font-black bg-rose-500/15 text-rose-700 rounded-full px-1.5 py-0.5">
                        {item.percent_off}% off
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    {r.email} · ${(r.amount_cents / 100).toFixed(2)} · {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 font-mono truncate">
                    session {r.session_id}
                  </div>
                  {refunded && (r.refunds || []).map((rf, i) => (
                    <div key={i} className="mt-1 flex items-center gap-1 text-[10px] text-rose-700 dark:text-rose-400">
                      <AlertTriangle className="w-3 h-3" />
                      Refunded ${(rf.amount_cents / 100).toFixed(2)} · {rf.reason || 'no reason'}
                    </div>
                  ))}
                </div>
                {!refunded && !isPending && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => refund(r)}
                    disabled={refunding === r.id}
                    className="rounded-full font-bold border-rose-500/40 text-rose-700 hover:bg-rose-500/10"
                    data-testid={`admin-credit-purchase-refund-${r.id}`}
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> {refunding === r.id ? 'Refunding…' : 'Refund + reverse credits'}
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
